import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useQuery } from '@tanstack/react-query';
import { HttpService } from '@/features/api/http.service';
import type { IDeal, IDealList } from '@/types/api';

const DEALS_PATH = '/deals';

/**
 * The mirror reads HubSpot every minute, so the payload changes at most that often — but polling on
 * the same period doubles the worst case a BD waits to see a stage move: up to a minute in the
 * mirror, then up to a minute here. Ten seconds keeps that tail near the mirror's own cadence, and
 * an unchanged mirror answers 304, so the five polls in between cost a round trip and no bytes.
 */
export const DEALS_REFRESH_INTERVAL_MS = 10_000;

export const dealsKeyFactory = createQueryKeys('deals', {
  list: () => ({
    queryKey: [DEALS_PATH],
    queryFn: () => HttpService.get<undefined, IDealList>(DEALS_PATH),
  }),
  getById: (dealId: string) => ({
    queryKey: [DEALS_PATH, dealId],
    queryFn: () => HttpService.get<string, IDeal>(`${DEALS_PATH}/${dealId}`),
  }),
});

/** One mirrored deal with its placement. */
export function useDealByIdQuery(dealId: string) {
  // A restored `?deal=` can point at a deal purged from the mirror; the
  // default retry:3 would hold the card's skeleton through the full backoff
  // before the error branch can dismiss it (same rationale as useZoneByIdQuery).
  return useQuery({ ...dealsKeyFactory.getById(dealId), retry: false });
}

/**
 * Every mirrored deal, placed or not: the map clusters the placed ones and the zone panel counts
 * them all. Polled every DEALS_REFRESH_INTERVAL_MS while the tab is focused — the response carries
 * an ETag, so an unchanged mirror costs a round trip and no bytes, and `synced_at` tells a caller
 * whether anything moved and therefore whether to re-index.
 *
 * `structuralSharing` is off on purpose: deep-comparing 15 000 objects to preserve references costs
 * more than replacing them, and callers key off `synced_at` instead.
 */
export function useDealsQuery(enabled: boolean) {
  return useQuery({
    ...dealsKeyFactory.list(),
    enabled,
    refetchInterval: DEALS_REFRESH_INTERVAL_MS,
    // Just under the interval, so the poll is what refetches rather than an incidental remount.
    staleTime: DEALS_REFRESH_INTERVAL_MS - 1_000,
    structuralSharing: false,
  });
}
